import { useGraphQLQuery } from '@betterment/graphql';
import { Box, Button, DropdownMenu, HStack, Icon, Link } from '@betterment/samba-react';
import { useRequiredParam } from '@betterment/shared-components/src/hooks/useRequiredParam';

import {
  AccountOverviewMenuDocument,
  AccountOverviewMenuQuery,
  AccountOverviewMenuQueryVariables,
} from './graphql/query.accountOverviewMenu.generated';

import {
  ENVELOPE_ACCOUNT_CASH_TYPE,
  ENVELOPE_ACCOUNT_DIRECT_TRADING_TYPE,
  ENVELOPE_ACCOUNT_MANAGED_TYPE,
} from '../../../Shared/constants';
import urlBuilder from '../../../Shared/url-builder';
import { navigate } from '../../../utils/path-helpers';
import FormButton from '../../FormButton';
import { DropdownImpersonationLink } from '../DropdownImpersonationLink';
import { isSingleRothIRAAccount, isSingleTraditionalIRAAccount } from '../HouseholdSummary/utils';
import { ImpersonationLink } from '../ImpersonationLink';

export const AccountOverviewCTAs = () => {
  const householdId = useRequiredParam('householdId');
  const accountId = useRequiredParam('accountId');

  const { data } = useGraphQLQuery<AccountOverviewMenuQuery, AccountOverviewMenuQueryVariables>(
    AccountOverviewMenuDocument,
    {
      variables: { id: accountId },
      throwOnError: true,
    }
  );

  if (!data) {
    return null;
  }

  const accountType = data.envelopeAccount.__typename;

  const validAccountTypes = [
    ENVELOPE_ACCOUNT_MANAGED_TYPE,
    ENVELOPE_ACCOUNT_CASH_TYPE,
    ENVELOPE_ACCOUNT_DIRECT_TRADING_TYPE,
  ];

  if (!validAccountTypes.includes(accountType)) {
    return null;
  }

  const isDirectTradingAccount = accountType === ENVELOPE_ACCOUNT_DIRECT_TRADING_TYPE;

  const depositUrl = urlBuilder.getImpersonateClientPage.absolute({
    clientId: data.envelopeAccount.users[0].id,
    deep_link_path: '/app/deposit_setup/destination',
  });

  const withdrawUrl = urlBuilder.getImpersonateClientPage.absolute({
    clientId: data.envelopeAccount.users[0].id,
    deep_link_path: '/app/withdrawal_setup',
  });

  const purposeId = data.envelopeAccount.envelope.purpose?.id;
  const adjustHoldingsPath = purposeId ? `/app/purpose/${purposeId}` : `/app/account/${accountId}`;

  const adjustHoldingsUrl = urlBuilder.getImpersonateClientPage.absolute({
    clientId: data.envelopeAccount.users[0].id,
    deep_link_path: adjustHoldingsPath,
  });

  const managedAccount =
    data.envelopeAccount.__typename === ENVELOPE_ACCOUNT_MANAGED_TYPE ? data.envelopeAccount : null;

  const hasEditablePortfolio = Boolean(managedAccount?.portfolio.isStrategySelectionEligible);
  const isRebalanceNowEligible = Boolean(managedAccount?.portfolio.rebalance.isRebalanceNowEligible);
  const isTraditionalIRA = isSingleTraditionalIRAAccount(managedAccount);
  const isRothIRA = isSingleRothIRAAccount(managedAccount);
  const goalId = managedAccount?.legacyId ?? null;

  return (
    <HStack css={{ gap: 'space.150', alignItems: 'center' }}>
      <DropdownMenu>
        <DropdownMenu.Trigger asChild={true}>
          <Button variant="solidSecondary" css={{ width: 'size.full', alignItems: 'center', verticalAlign: 'middle' }}>
            <HStack>
              Manage
              <Icon name="CaretDown" size="small" />
            </HStack>
          </Button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content align="end" css={{ minWidth: '240px' }}>
          {!isDirectTradingAccount && (
            <>
              {hasEditablePortfolio && goalId && (
                <DropdownMenu.Item key="edit portfolio">
                  <Box css={{ width: 'size.full' }}>
                    <FormButton
                      method="post"
                      variant="textSecondary"
                      css={{ textDecoration: 'none', width: 'size.full' }}
                      action={urlBuilder.getEditPortfolioPage.absolute({
                        goal_id: goalId,
                        return_to_path: urlBuilder.getAccountOverviewPage(householdId, accountId),
                      })}
                    >
                      Edit portfolio
                    </FormButton>
                  </Box>
                </DropdownMenu.Item>
              )}
              {isTraditionalIRA && managedAccount && (
                <DropdownMenu.Item key="convert to roth">
                  <Link
                    css={{ textDecoration: 'none' }}
                    variant="textSecondary"
                    href={urlBuilder.getHouseholdConvertFundsToRoth({
                      householdId,
                      subAccountId: managedAccount.subAccounts[0].legacySubAccountId,
                    })}
                  >
                    Convert funds to roth
                  </Link>
                </DropdownMenu.Item>
              )}
              {isRothIRA && managedAccount && (
                <DropdownImpersonationLink
                  showModal={false}
                  url={urlBuilder.getImpersonateClientPage.absolute({
                    clientId: managedAccount.users[0].id,
                    deep_link_path: `/ira_recharacterizations`,
                  })}
                  label="Recharacterize contributions"
                  css={{ width: 'size.full' }}
                />
              )}
              {isRebalanceNowEligible && goalId && (
                <DropdownMenu.Item
                  key="rebalance account"
                  onSelect={() => {
                    navigate(urlBuilder.getRebalanceNowPage.absolute({ goalId }));
                  }}
                >
                  Rebalance account
                </DropdownMenu.Item>
              )}
              <DropdownImpersonationLink
                label="Deposit"
                url={depositUrl}
                showModal={true}
                css={{ width: 'size.full' }}
              />
            </>
          )}
          {isDirectTradingAccount && (
            <DropdownImpersonationLink
              label="Adjust holdings"
              url={adjustHoldingsUrl}
              showModal={true}
              css={{ width: 'size.full' }}
            />
          )}
          <DropdownImpersonationLink label="Withdraw" url={withdrawUrl} showModal={true} css={{ width: 'size.full' }} />
        </DropdownMenu.Content>
      </DropdownMenu>
      <ImpersonationLink
        showModal={true}
        url={depositUrl}
        label="Deposit"
        variant="solidPrimary"
        css={{ width: 'size.full' }}
      />
    </HStack>
  );
};
